import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, 
  TextField, 
  InputAdornment,
  IconButton,
  Card,
  CardContent,
  Typography,
  CardMedia,
  CircularProgress,
  Alert,
  Link as MuiLink,
  Paper
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import debounce from 'lodash/debounce';
import { Link as RouterLink } from 'react-router-dom';
import Link from '@mui/material/Link';
import { searchBooksByKeyword } from '../api/bookSearch';
import { collection, query, orderBy, limit, getDocs, doc, getDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import SEO from './SEO';

function Search() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [highlights, setHighlights] = useState([]);
  const lastHighlightRef = useRef(null);
  const inputRef = useRef();
  const [userMap, setUserMap] = useState({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const containerRef = useRef(null);
  
  // 캐싱을 위한 ref
  const highlightsCache = useRef(new Map());
  const lastFetchTime = useRef(0);
  const CACHE_DURATION = 5 * 60 * 1000; // 5분 캐시

  // Fisher-Yates 셔플 알고리즘 (메모이제이션)
  const fisherYatesShuffle = useCallback((array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, []);

  // 시간대별 샘플링 전략 (메모이제이션)
  const getTimeRanges = useCallback(() => {
    const now = Date.now();
    const oneWeek = 7 * 24 * 60 * 60 * 1000;
    const oneMonth = 30 * 24 * 60 * 60 * 1000;
    const threeMonths = 90 * 24 * 60 * 60 * 1000;
    
    return [
      { start: new Date(now - oneWeek), end: new Date(now) }, // 최근 1주
      { start: new Date(now - oneMonth), end: new Date(now - oneWeek) }, // 1주-1개월
      { start: new Date(now - threeMonths), end: new Date(now - oneMonth) }, // 1개월-3개월
      { start: new Date(0), end: new Date(now - threeMonths) } // 3개월 이상
    ];
  }, []);

  const fetchHighlights = async (pageNum = 1, append = false) => {
    try {
      // 캐시 확인
      const now = Date.now();
      const cacheKey = `highlights_${pageNum}`;
      const cached = highlightsCache.current.get(cacheKey);
      
      if (cached && (now - lastFetchTime.current) < CACHE_DURATION) {
        if (append) {
          setHighlights(prev => [...prev, ...cached]);
        } else {
          setHighlights(cached);
        }
        setHasMore(cached.length === 10);
        return;
      }

      const highlightsRef = collection(db, 'highlights');
      
      // 진정한 랜덤 샘플링을 위한 전략
      let selectedHighlights = [];
      
      // 전략 1: 전체 컬렉션에서 랜덤 샘플링 (주 전략)
      try {
        // 전체 컬렉션에서 충분한 샘플을 가져와서 랜덤 선택
        const totalQuery = query(highlightsRef, limit(2000)); // 더 많은 샘플을 가져와서 진정한 랜덤 보장
        const totalSnapshot = await getDocs(totalQuery);
        const allHighlights = totalSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Fisher-Yates 셔플로 진정한 랜덤 구현
        const shuffled = fisherYatesShuffle(allHighlights);
        selectedHighlights = shuffled.slice(0, 10);
        
      } catch (mainError) {
        console.warn('주 랜덤 샘플링 실패:', mainError);
        
        // 전략 2: 여러 시간대에서 균등하게 샘플링 (백업 전략)
        const timeRanges = getTimeRanges();
        const samplesPerRange = Math.ceil(10 / timeRanges.length); // 균등 분배
        
        for (const timeRange of timeRanges) {
          try {
            const q = query(
              highlightsRef,
              where('createdAt', '>=', timeRange.start),
              where('createdAt', '<=', timeRange.end),
              orderBy('createdAt', 'desc'),
              limit(samplesPerRange * 3) // 더 많은 샘플을 가져와서 랜덤 선택
            );
            
            const snapshot = await getDocs(q);
            const rangeHighlights = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            if (rangeHighlights.length > 0) {
              const shuffled = fisherYatesShuffle(rangeHighlights);
              const selected = shuffled.slice(0, samplesPerRange);
              selectedHighlights.push(...selected);
            }
          } catch (rangeError) {
            console.warn('특정 시간대 샘플링 실패:', rangeError);
            continue;
          }
        }
      }
      
      // 전략 3: 최근 하이라이트로 폴백 (최후의 수단)
      if (selectedHighlights.length < 5) {
        try {
          const fallbackQuery = query(
            highlightsRef,
            orderBy('createdAt', 'desc'),
            limit(50)
          );
          const fallbackSnapshot = await getDocs(fallbackQuery);
          const fallbackHighlights = fallbackSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          const shuffled = fisherYatesShuffle(fallbackHighlights);
          const additional = shuffled.slice(0, 10 - selectedHighlights.length);
          selectedHighlights.push(...additional);
        } catch (fallbackError) {
          console.warn('폴백 샘플링 실패:', fallbackError);
        }
      }
      
      // 최종 결과 처리 및 중복 제거
      const uniqueHighlights = selectedHighlights.filter((highlight, index, self) => 
        index === self.findIndex(h => h.id === highlight.id)
      );
      
      const finalHighlights = uniqueHighlights.slice(0, 10);
      
      // 캐시에 저장
      highlightsCache.current.set(cacheKey, finalHighlights);
      lastFetchTime.current = now;
      
      if (append) {
        setHighlights(prev => [...prev, ...finalHighlights]);
      } else {
        setHighlights(finalHighlights);
      }

      // 더 불러올 하이라이트가 있는지 확인
      setHasMore(finalHighlights.length === 10);
      
    } catch (err) {
      console.error('하이라이트 불러오기 오류:', err);
      if (!append) {
        setHighlights([]);
      }
    }
  };

  const debouncedSearch = useCallback(
    debounce(async (term) => {
      if (!term.trim()) {
        setSearchResults([]);
        return;
      }
  
      setLoading(true);
      setError(null);
  
      try {
        // 환경에 따른 API URL 설정
        const isDevelopment = process.env.NODE_ENV === 'development';
        const baseUrl = isDevelopment 
          ? 'https://asia-northeast3-booking-6ec89.cloudfunctions.net'
          : '';
        
        const apiUrl = `${baseUrl}/aladinSearch?query=${encodeURIComponent(term)}&type=Title&MaxResults=10`;
        console.log('API 호출 URL:', apiUrl);
        
        // 알라딘 프록시 API 호출
        const response = await fetch(apiUrl);
        
        console.log('응답 상태:', response.status);
        console.log('응답 헤더:', Object.fromEntries(response.headers.entries()));
  
        // 응답을 text로 우선 파싱하여 HTML 오류 여부 확인
        const rawText = await response.text();
  
        if (rawText.startsWith('<!DOCTYPE') || rawText.includes('<html')) {
          console.error('HTML 응답 내용:', rawText.substring(0, 1000));
          throw new Error('알라딘 API 오류: HTML 페이지가 반환되었습니다. (API 키 만료 또는 호출 제한 가능)');
        }
  
        // JSON으로 파싱
        let data;
        try {
          data = JSON.parse(rawText);
        } catch (parseError) {
          console.error('JSON 파싱 오류:', parseError);
          console.error('응답 내용:', rawText.substring(0, 1000));
          throw new Error('알라딘 API 응답 파싱 실패');
        }
  
        console.log('파싱된 데이터:', data);
  
        // 알라딘 응답 파싱 및 기존 UI 구조에 맞게 매핑
        const processedResults = (data.item || []).map(item => ({
          title: item.title || '',
          authors: Array.isArray(item.author) ? item.author : (item.author ? item.author.split(';').map(a => a.trim()).filter(Boolean) : []),
          isbn: item.isbn13 || '',
          cleanedIsbn: item.isbn13 || '',
          thumbnail: item.cover || '',
          publisher: item.publisher || '',
          priceSales: item.priceSales || 0,
          priceStandard: item.priceStandard || 0,
          itemId: item.itemId || '',
          description: item.description || '',
          pubDate: item.pubDate || '',
          categoryName: item.categoryName || '',
          customerReviewRank: item.customerReviewRank || 0
        })).filter(item => item.cleanedIsbn);
  
        setSearchResults(processedResults);
      } catch (error) {
        console.error('알라딘 검색 오류:', error);
        setError(error.message || '알 수 없는 오류');
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    }, 1000),
    []
  );

  useEffect(() => {
    debouncedSearch(searchTerm);
    return () => debouncedSearch.cancel();
  }, [searchTerm, debouncedSearch]);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setPage(1);
      fetchHighlights(1, false);
    }
  }, [searchTerm]);

  useEffect(() => {
    // 페이지 로드 시 자동으로 검색창에 포커스하지 않음
    // focusSearch 상태가 있을 때만 포커스
  }, []);

  useEffect(() => {
    if (location.state?.focusSearch && inputRef.current) {
      // 약간의 딜레이를 두어 페이지 렌더링 완료 후 포커스
      setTimeout(() => {
        inputRef.current.focus();
      }, 100);
    }
  }, [location.state]);

  useEffect(() => {
    // highlights가 바뀔 때마다 uid 목록 추출
    const userIds = Array.from(new Set(highlights.map(h => h.uid || h.userId).filter(Boolean)));
    if (userIds.length === 0) {
      setUserMap({});
      return;
    }

    let isMounted = true;
    Promise.all(userIds.map(uid => getDoc(doc(db, 'users', uid))))
      .then(docs => {
        if (!isMounted) return;
        const map = {};
        docs.forEach(docSnap => {
          if (docSnap.exists()) {
            map[docSnap.id] = docSnap.data().displayName || '익명';
          }
        });
        setUserMap(map);
      });

    return () => { isMounted = false; };
  }, [highlights]);

  const handleBookClick = (book) => {
    if (!book?.cleanedIsbn) {
      setError("책 상세 정보로 이동할 수 없습니다. (ISBN 오류)");
      return;
    }
    navigate(`/booksearchdetail/${book.cleanedIsbn}`);
  };

  // 스크롤 이벤트 핸들러
  const handleScroll = useCallback(
    debounce(() => {
      if (!containerRef.current || isLoadingMore || !hasMore || searchTerm.trim()) return;

      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      if (scrollPercentage >= 0.8) {
        setIsLoadingMore(true);
        const nextPage = page + 1;
        setPage(nextPage);
        
        fetchHighlights(nextPage, true).finally(() => {
          setIsLoadingMore(false);
        });
      }
    }, 200),
    [isLoadingMore, hasMore, page, searchTerm, fetchHighlights]
  );

  // 스크롤 이벤트 리스너 등록
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <>
      <SEO 
        title="책 검색 | 스냅로그"
        description="책 제목, 저자, 출판사로 원하는 책을 검색하세요. 스냅로그에서 독서기록을 시작해보세요."
        url="/search"
      />
      <Box 
        ref={containerRef}
        sx={{ pb: 0, pt: 0, px: 0, maxWidth: '600px', margin: 0, width: '100%' }}
      >
      <Box sx={{ p: 0 }}>
        <TextField
          inputRef={inputRef}
          fullWidth
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="책 제목, 저자, 출판사 검색"
          variant="outlined"
          disabled={loading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {loading && <CircularProgress size={24} />}
                <SearchIcon color="action" />
              </InputAdornment>
            )
          }}
          sx={{ 
            px: 2,
            pt: 2,
            pb:0,
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: 'background.paper'
            }
          }}
        />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* 🔍 검색 결과 카드 렌더링 */}
        {searchResults.length > 0 && searchResults.map((book) => (
          <Paper
            key={book.cleanedIsbn}
            elevation={0}
            onClick={() => handleBookClick(book)}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 2,
              cursor: 'pointer',
              py: 2,
              px: 2,
              borderBottom: '1px solid #e5e5e5',
              transition: 'background-color 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: '#f9f9f9',
              },
              '&:last-child': {
                borderBottom: 'none',
              },
              mb: 0,
              boxShadow: 'none',
              borderRadius: 0,
              background: '#fff',
            }}
          >
            <Box sx={{ flexShrink: 0, width: 70, position: 'relative' }}>
              <img
                src={book.thumbnail || '/images/no-cover.png'}
                alt={`${book.title}${book.authors ? ` - ${book.authors.join(', ')}` : ''}`}
                loading="lazy"
                style={{ width: '100%', height: 'auto', display: 'block', backgroundColor: '#f5f5f5', borderRadius: 0 }}
              />
            </Box>
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' }}>
              <Typography 
                variant="body1"
                sx={{ 
                  fontWeight: 'medium',
                  fontSize: '15px',
                  color: '#333',
                  mb: 0.2,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'break-word',
                }} 
              >
                {book.title}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '12px',
                  color: '#888',
                  mb: 0.1,
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'break-word',
                }}
              >
                {book.authors?.join(', ') || '저자 정보 없음'}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '12px',
                  color: '#888',
                  mb: 0.1,
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wordBreak: 'break-word',
                }}
              >
                {book.publisher || '출판사 정보 없음'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '12px', color: '#888', mt: 0.1 }}>
                <Typography variant="caption" sx={{ color: 'inherit' }}>
                  ISBN: {book.cleanedIsbn}
                </Typography>
              </Box>
            </Box>
          </Paper>
        ))}

        {/* 🌟 다른 유저의 하이라이트 카드 렌더링 */}
        {!searchTerm.trim() && (
          <Box sx={{ px: 0, pt: 1, pb: 0 }} >
            <Typography variant="h6" sx={{
              px: 2,
              mb: -1,
              fontWeight: 700,
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'normal',
            }}>다른 사람의 기록</Typography>
            {highlights.map((highlight, idx) => {
              const userCode = highlight.uid || highlight.userId;
              const isbn = highlight.bookId || highlight.bookIsbn || highlight.isbn || '';
              const bookDetailPath = `/book/${userCode}_${isbn}?uid=${userCode}&highlightId=${highlight.id}`;
              const userProfilePath = `/u/${userCode}`;

              // 개선된 클릭 핸들러
              const handleCardClick = (e) => {
                if (
                  e.target.dataset.nickname ||
                  e.target.dataset.preventcard ||
                  e.target.closest('[data-preventcard]')
                ) return;
                
                // 스크롤 위치를 기억하기 위해 sessionStorage에 저장
                sessionStorage.setItem('scrollPosition', window.scrollY.toString());
                
                // 브라우저 호환성을 위한 전환 효과
                try {
                  // 페이지 이동 시 부드러운 전환을 위한 클래스 추가
                  document.body.style.transition = 'opacity 0.2s ease-out';
                  document.body.style.opacity = '0.8';
                } catch (error) {
                  console.warn('Transition effect failed:', error);
                }
                
                // 페이지 이동
                navigate(bookDetailPath);
                
                // 전환 효과 복원
                setTimeout(() => {
                  try {
                    document.body.style.transition = '';
                    document.body.style.opacity = '';
                  } catch (error) {
                    console.warn('Transition cleanup failed:', error);
                  }
                }, 200);
              };

              return (
                <Card
                  key={highlight.id}
                  id={`highlight-${highlight.id}`}
                  ref={idx === highlights.length - 1 ? lastHighlightRef : null}
                  sx={{
                    mb: 2,
                    cursor: 'pointer',
                    backgroundColor: '#fff',
                    boxShadow: 'none',
                    border: 'none',
                    borderBottom: '1px solid #ececec',
                    transition: 'box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out', // transform 애니메이션 추가
                    '&:hover': {
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                      backgroundColor: '#f7f7f7',
                      transform: 'translateY(-1px)', // 호버 시 살짝 위로 이동
                    },
                    '&:active': {
                      transform: 'translateY(0px) scale(0.99)', // 클릭 시 살짝 축소
                    },
                    p: 0,
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                  }}
                  onClick={handleCardClick}
                >
                  <CardContent sx={{
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    px: 2,
                    py: 2.5,
                  }}>
                    <Typography
                      variant="h7"
                      sx={{
                        fontWeight: 500,
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 5,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        lineHeight: 1.8,
                      }}
                    >
                      {(highlight.text || '').replace(/\n/g, ' ')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', minHeight: 72 }}>
                      <CardMedia
                        component="img"
                        sx={{
                          width: 48,
                          height: 72,
                          objectFit: 'cover',
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                          borderTopRightRadius: 6,
                          borderBottomRightRadius: 6,
                          boxShadow: '4px 2px 8px 0 rgba(0,0,0,0.10)',
                          alignSelf: 'stretch',
                          bgcolor: '#f5f5f5'
                        }}
                        image={highlight.bookThumbnail || '/images/no-cover.png'}
                        alt="책 표지"
                      />
                      <Box sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        height: 72
                      }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            fontSize: '0.92rem',
                            color: 'text.primary',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'normal',
                          }}
                        >
                          {highlight.bookTitle || '제목 없음'}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 0.5,
                            color: 'text.primary',
                            fontWeight: 400,
                            fontSize: '0.85em',
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'normal',
                          }}
                        >
                          {highlight.bookAuthors?.join(', ') || '작가 정보 없음'}
                        </Typography>
                        <MuiLink
                          component={RouterLink}
                          to={userProfilePath}
                          data-nickname
                          underline="hover"
                          onClick={e => e.stopPropagation()}
                          sx={{
                            fontSize: '0.87em',
                            cursor: 'pointer',
                            fontWeight: 500,
                            color: '#555555',
                            transition: 'all 0.2s ease-in-out',
                            textDecoration: 'none',
                            fontFamily: 'NanumSquareB, sans-serif',
                            letterSpacing: '-0.01em',
                            '&:hover': { 
                              color: '#FFD700', 
                              textDecoration: 'underline',
                              textUnderlineOffset: '2px',
                              textDecorationColor: '#FFD700',
                              textDecorationThickness: '1.5px',
                            },
                            '&:active': {
                              color: '#FFC107',
                              transform: 'scale(0.98)',
                            },
                          }}
                        >
                          {userMap[highlight.uid] || highlight.userName || '익명'}님의 문장
                        </MuiLink>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
            
            {/* 로딩 인디케이터 */}
            {isLoadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
    </>
  );
}

export default Search;
